import PollDetailsHero from '@components/common/PollDetailsHero'
import { EditorGrid, EditorSectionHeading } from '@components/common/EditorWorkspace'
import PollDesign from '@components/common/PollDesign'
import ViewNavigation from '@components/common/ViewNavigation'
import Box from '@mui/material/Box'
import TextField from '@mui/material/TextField'
import MenuItem from '@mui/material/MenuItem'
import Autocomplete from '@mui/material/Autocomplete'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import Chip from '@mui/material/Chip'
import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import {
  DndContext,
  closestCenter,
  useSensor,
  useSensors,
  PointerSensor,
  TouchSensor,
} from '@dnd-kit/core'
import {
  SortableContext,
  arrayMove,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { GlassButton, GlassOutlineButton, GlassCard, GlassPanel } from '@components/common/Glass'
import RankedItemCard from '@components/common/RankedItemCard'
import SaveRoundedIcon from '@mui/icons-material/SaveRounded'
import { Poll } from 'src/api/types/polls'
import {
  deletePoll,
  getPoll,
  getPollCategories,
  getPollTags,
  ratePoll,
  removePollSubmission,
  submitPoll,
  updatePollItems,
  updatePoll,
} from 'src/api/polls'
import { err, split } from 'src/utils/functions'
import DragIndicatorIcon from '@mui/icons-material/DragIndicator'
import AddIcon from '@mui/icons-material/AddRounded'
import SwapVertRoundedIcon from '@mui/icons-material/SwapVertRounded'
import ReplyRoundedIcon from '@mui/icons-material/ReplyRounded'
import CloseRoundedIcon from '@mui/icons-material/CloseRounded'
import Button from '@mui/material/Button'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import DeleteForeverRoundedIcon from '@mui/icons-material/DeleteForeverRounded'
import HowToVoteRoundedIcon from '@mui/icons-material/HowToVoteRounded'
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import AutorenewRoundedIcon from '@mui/icons-material/AutorenewRounded'
import PollLoadingView from '@components/common/PollLoadingView'
import PollNavButton from '@components/common/PollNavButton'
import Collapse from '@mui/material/Collapse'
import InputAdornment from '@mui/material/InputAdornment'
import {
  altColor,
  altColorDarkText,
  helperTextColor,
  mainColor,
  mainColorDarker,
  mainColorMediumText,
  shadow2,
  shadow1,
} from 'src/ui/theme/tokens'
import GroupsRoundedIcon from '@mui/icons-material/GroupsRounded'
import Avatar from '@mui/material/Avatar'
import useMediaQuery from '@mui/material/useMediaQuery'
import useTheme from '@mui/material/styles/useTheme'
import IconButton from '@mui/material/IconButton'
import ThumbUpRoundedIcon from '@mui/icons-material/ThumbUpRounded'
import ThumbDownRoundedIcon from '@mui/icons-material/ThumbDownRounded'
import ClearRoundedIcon from '@mui/icons-material/ClearRounded'
import AddCircleOutlineRoundedIcon from '@mui/icons-material/AddCircleOutlineRounded'
import VisibilityRoundedIcon from '@mui/icons-material/VisibilityRounded'
import VisibilityOffRoundedIcon from '@mui/icons-material/VisibilityOffRounded'
import useWaterfallIntro from 'src/hooks/useWaterfallIntro'
import Tooltip from '@mui/material/Tooltip'

const PollSortableItem = ({
  id,
  index,
  title,
  onSwapClick,
  onSwapHover,
  isSwapMode,
  isSwapSelected,
  isSwapHovered,
  isDisabled,
  swapControl,
  swapAnimationDirection,
  swapPhase,
}: {
  id: string
  index: number
  title: string
  onSwapClick: (id: string) => void
  onSwapHover: (id: string | null) => void
  isSwapMode: boolean
  isSwapSelected: boolean
  isSwapHovered: boolean
  isDisabled?: boolean
  swapControl?: ReactNode
  swapAnimationDirection?: 'up' | 'down'
  swapPhase?: 'out' | 'in' | null
}) => {
  const theme = useTheme()
  const fullScreen = useMediaQuery(theme.breakpoints.down('md'))
  const { setNodeRef, attributes, listeners, transform, transition, isDragging } = useSortable({
    id,
    disabled: isSwapMode || isDisabled,
  })
  const swapActiveStyles =
    isSwapSelected || isSwapHovered
      ? {
          borderColor: 'rgba(83,217,245,0.65)',
          boxShadow: '0 3px 0 #e0eaf2, 0 8px 18px #1c3b5709',
          outline: '1.5px solid rgba(83,217,245,0.55)',
          outlineOffset: '0px',
          transform: 'scale(1.012)',
          animation: 'swapFloat 2.4s ease-in-out infinite',
          '@keyframes swapFloat': {
            '0%': {
              transform: 'translateY(0) scale(1.012)',
              boxShadow: '0 3px 0 #e0eaf2, 0 8px 18px #1c3b5709',
            },
            '50%': {
              transform: 'translateY(-2px) scale(1.016)',
              boxShadow: '0 3px 0 #e0eaf2, 0 8px 18px #1c3b5709',
            },
            '100%': {
              transform: 'translateY(0) scale(1.012)',
              boxShadow: '0 3px 0 #e0eaf2, 0 8px 18px #1c3b5709',
            },
          },
        }
      : undefined
  const swapAnimStyles =
    swapAnimationDirection && swapPhase
      ? {
          animation:
            swapAnimationDirection === 'down'
              ? swapPhase === 'out'
                ? 'swapDownOut 160ms ease-in'
                : 'swapDownIn 220ms ease-out'
              : swapPhase === 'out'
                ? 'swapUpOut 160ms ease-in'
                : 'swapUpIn 220ms ease-out',
          '@keyframes swapDownOut': {
            '0%': { opacity: 1, transform: 'translateY(0) scale(1.01)' },
            '100%': { opacity: 0, transform: 'translateY(50px) scale(0.99)' },
          },
          '@keyframes swapDownIn': {
            '0%': { opacity: 0, transform: 'translateY(-50px) scale(0.99)' },
            '100%': { opacity: 1, transform: 'translateY(0) scale(1)' },
          },
          '@keyframes swapUpOut': {
            '0%': { opacity: 1, transform: 'translateY(0) scale(1.01)' },
            '100%': { opacity: 0, transform: 'translateY(-50px) scale(0.99)' },
          },
          '@keyframes swapUpIn': {
            '0%': { opacity: 0, transform: 'translateY(50px) scale(0.99)' },
            '100%': { opacity: 1, transform: 'translateY(0) scale(1)' },
          },
        }
      : undefined

  const raiseLayer = isDragging || isSwapSelected || isSwapHovered
  return (
    <Box
      ref={setNodeRef}
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
        position: 'relative',
        zIndex: raiseLayer ? 20 : 1,
      }}
      onClick={() => onSwapClick(id)}
      onMouseEnter={() => onSwapHover(id)}
      onMouseLeave={() => onSwapHover(null)}
    >
      <RankedItemCard
        index={index + 1}
        title={title}
        tone="neutral"
        isDragging={isDragging}
        className="swap-card"
        fullScreen={fullScreen}
        cardSx={{
          cursor: 'pointer',
          ...(swapActiveStyles || {}),
          ...(swapAnimStyles || {}),
        }}
        rightAdornment={
          <Box
            {...attributes}
            {...listeners}
            sx={{
              cursor: isSwapMode ? 'default' : 'grab',
              px: 1,
              color: '#9ca3af',
              touchAction: 'none',
              pointerEvents: isSwapMode ? 'none' : 'auto',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              height: '100%',
            }}
          >
            {swapControl ? swapControl : <DragIndicatorIcon fontSize="small" />}
          </Box>
        }
      />
    </Box>
  )
}

export default function PollDetailSection({
  pollId,
  onBack,
  onPlayPoll,
  onPollBack,
  onBackToDetail,
  pollBackLabel = 'Polls',
  onNewPoll,
  topRightLabel,
  onOpenHowToPlay,
  sounds,
  userId,
  onPollDeleted,
  externalLoading = false,
  skipFetch = false,
  initialPoll,
}: {
  pollId: number
  onBack?: () => void
  onPlayPoll?: (poll: Poll) => void
  onPollBack?: () => void
  onBackToDetail?: () => void
  pollBackLabel?: string
  onNewPoll?: () => void
  topRightLabel?: string
  onOpenHowToPlay?: () => void
  sounds?: {
    click?: () => void
    submit?: () => void
    drag?: () => void
    celebrate?: () => void
    start?: () => void
  }
  userId?: number
  onPollDeleted?: (pollId: number) => void
  externalLoading?: boolean
  skipFetch?: boolean
  initialPoll?: Poll
}): JSX.Element {
  const [poll, setPoll] = useState<Poll | undefined>()
  const [loading, setLoading] = useState(true)
  const [rankIds, setRankIds] = useState<string[]>([])
  const [hasSubmitted, setHasSubmitted] = useState(false)
  const [showEditor, setShowEditor] = useState(false)
  const [editorMode, setEditorMode] = useState<'main' | 'extra' | null>(null)
  const [editingExtraIndex, setEditingExtraIndex] = useState<number | null>(null)
  const [ownerExtras, setOwnerExtras] = useState<
    { id?: number; ranking: (number | string)[]; ownerExtraIndex?: number }[]
  >([])
  const [swapFromId, setSwapFromId] = useState<string | null>(null)
  const [swapHoverId, setSwapHoverId] = useState<string | null>(null)
  const [swapAnimatingIds, setSwapAnimatingIds] = useState<string[]>([])
  const [swapAnimationDirectionById, setSwapAnimationDirectionById] = useState<
    Record<string, 'up' | 'down'>
  >({})
  const [swapPhase, setSwapPhase] = useState<'out' | 'in' | null>(null)
  const [deleteOpen, setDeleteOpen] = useState(false)
  const [tagOptions, setTagOptions] = useState<string[]>([])
  const [categoryOptions, setCategoryOptions] = useState<string[]>([])
  const [isUpdatingMeta, setIsUpdatingMeta] = useState(false)
  const [introEnabled, setIntroEnabled] = useState(false)
  const [metaDraft, setMetaDraft] = useState<{
    category: string
    tags: string
    visibility: string
    pollResultsVisibility: 'hidden' | 'visible'
  }>({
    category: '',
    tags: '',
    visibility: 'public',
    pollResultsVisibility: 'visible',
  })
  const [itemDrafts, setItemDrafts] = useState<{ id?: number; title: string }[]>([])
  const [isUpdatingItems, setIsUpdatingItems] = useState(false)
  const [pollRated, setPollRated] = useState<number | undefined>(undefined)
  const [communityResultsOpen, setCommunityResultsOpen] = useState(false)
  const listTopRef = useRef<HTMLDivElement | null>(null)
  const promptRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    if (typeof window === 'undefined') return
    const timer = window.setTimeout(() => {
      window.requestAnimationFrame(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' })
      })
    }, 40)
    return () => window.clearTimeout(timer)
  }, [pollId, showEditor, hasSubmitted, editorMode])

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(TouchSensor, {
      activationConstraint: { delay: 120, tolerance: 6 },
    })
  )

  const itemsById = useMemo(() => {
    const mapping: Record<string, string> = {}
    poll?.items?.forEach((item) => {
      const id = String(item.id ?? item.index)
      mapping[id] = item.title
    })
    return mapping
  }, [poll])

  const shuffleIds = (ids: string[]) => {
    const next = ids.slice()
    for (let i = next.length - 1; i > 0; i -= 1) {
      const j = Math.floor(Math.random() * (i + 1))
      const temp = next[i]
      if (!next[i] || !next[j] || !temp) return []
      next[i] = next[j]
      next[j] = temp
    }
    return next
  }

  const introKey = useMemo(() => {
    const ids = (poll?.items || []).map((item) => String(item.id ?? item.index)).sort()
    const phase = showEditor ? 'vote' : 'play'
    return `${poll?.id ?? pollId ?? 'poll'}:${phase}:${ids.join('|')}`
  }, [poll?.id, pollId, poll?.items, showEditor])

  const { visibleCount, isAnimating: isIntroAnimating } = useWaterfallIntro({
    key: introKey,
    count: rankIds.length,
    enabled: showEditor && introEnabled && rankIds.length > 0,
    onStep: () => sounds?.drag?.(),
    onComplete: () => setIntroEnabled(false),
  })

  const makeLocalKey = () => {
    if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
      return `poll_${pollId}_${crypto.randomUUID()}`
    }
    return `poll_${pollId}_${Math.random().toString(36).slice(2)}`
  }

  const isOwner = !!poll?.creatorId && !!userId && poll.creatorId === userId
  const normalizedPollResultsVisibility = poll?.pollResultsVisibility || 'visible'
  const canViewCommunityResults =
    isOwner || (normalizedPollResultsVisibility === 'visible' && poll?.dailyPollType !== 'today')
  const viewerRankingIds = useMemo(() => {
    if (!Array.isArray(poll?.viewerSubmission?.ranking)) return []
    return poll.viewerSubmission.ranking.map((entry) => String(entry))
  }, [poll?.viewerSubmission])
  const viewerRankingTitles = useMemo(
    () =>
      viewerRankingIds
        .map((id) => poll?.items?.find((item) => String(item.id ?? item.index) === id)?.title)
        .filter(Boolean) as string[],
    [poll?.items, viewerRankingIds]
  )
  const communityResultsRows = useMemo(() => {
    if (!poll?.items?.length || !Array.isArray(poll.crowdRank)) return []
    return poll.crowdRank.map((id, idx) => {
      const item = poll.items?.find((entry) => String(entry.id ?? entry.index) === String(id))
      if (!item) return undefined
      const avg = poll.stats?.itemAverages?.[String(id)]
      return {
        id: String(id),
        rank: idx + 1,
        title: item.title,
        average: typeof avg === 'number' ? avg : undefined,
        stdDev:
          typeof poll.stats?.itemStdDevs?.[String(id)] === 'number'
            ? poll.stats?.itemStdDevs?.[String(id)]
            : undefined,
        top3Count:
          typeof poll.stats?.top3Counts?.[String(id)] === 'number'
            ? poll.stats?.top3Counts?.[String(id)]
            : undefined,
      }
    })
  }, [
    poll?.crowdRank,
    poll?.items,
    poll?.stats?.itemAverages,
    poll?.stats?.itemStdDevs,
    poll?.stats?.top3Counts,
  ]).filter(Boolean) as {
    id: string
    rank: number
    title: string
    average?: number
    stdDev?: number
    top3Count?: number
  }[]
  const maxTop3Count = useMemo(
    () => communityResultsRows.reduce((max, row) => Math.max(max, Number(row.top3Count || 0)), 0),
    [communityResultsRows]
  )
  const crowdFavoriteId = useMemo(() => {
    if (!communityResultsRows.length) return undefined
    let best: { id: string; score: number; rank: number } | undefined
    communityResultsRows.forEach((row) => {
      const score = Number(row.top3Count || 0)
      if (!best || score > best.score || (score === best.score && row.rank < best.rank)) {
        best = { id: row.id, score, rank: row.rank }
      }
    })
    return best?.score && best.score > 0 ? best.id : undefined
  }, [communityResultsRows])
  const mostControversialId = useMemo(() => {
    if (!communityResultsRows.length) return undefined
    let best: { id: string; score: number; rank: number } | undefined
    communityResultsRows.forEach((row) => {
      const score = Number(row.stdDev || 0)
      if (!best || score > best.score || (score === best.score && row.rank < best.rank)) {
        best = { id: row.id, score, rank: row.rank }
      }
    })
    return best?.score && best.score > 0 ? best.id : undefined
  }, [communityResultsRows])
  const selectedCategories = useMemo(
    () => (metaDraft.category ? split(metaDraft.category, ',') : []),
    [metaDraft.category]
  )

  const selectedTags = useMemo(
    () => (metaDraft.tags ? split(metaDraft.tags, ',') : []),
    [metaDraft.tags]
  )
  const mergedCategoryOptions = useMemo(
    () =>
      Array.from(
        new Set([
          ...(categoryOptions || []),
          ...selectedCategories,
          ...(metaDraft.category ? split(metaDraft.category, ',') : []),
        ])
      ).filter(Boolean),
    [categoryOptions, selectedCategories, metaDraft.category]
  )
  const mergedTagOptions = useMemo(
    () =>
      Array.from(
        new Set([
          ...(tagOptions || []),
          ...selectedTags,
          ...(metaDraft.tags ? split(metaDraft.tags, ',') : []),
        ])
      ).filter(Boolean),
    [tagOptions, selectedTags, metaDraft.tags]
  )
  const normalizeMetaValue = (value: string) => value.trim().slice(0, 16)
  const normalizeMetaList = (value: string[]) =>
    value.map(normalizeMetaValue).filter(Boolean).slice(0, 3)
  const guestKeyStorage = pollId ? `poll_guest_key_${pollId}` : ''
  const getGuestKey = () => {
    if (userId) return undefined
    if (!guestKeyStorage) return undefined
    let key = localStorage.getItem(guestKeyStorage) || ''
    if (!key) {
      key = makeLocalKey()
      localStorage.setItem(guestKeyStorage, key)
    }
    return key
  }

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setLoading(true)
    const localKey = getGuestKey()
    if (skipFetch || !pollId) return
    const request =
      initialPoll?.id === pollId
        ? Promise.resolve({ success: true, data: initialPoll })
        : getPoll(pollId, localKey ? { localKey } : undefined)
    request
      .then((res) => {
        if (res.success && res.data) {
          setPoll(res.data)
          const ids = (res.data.items || []).map((item) => String(item.id ?? item.index))
          setOwnerExtras(res.data.ownerExtras || [])
          const viewerRanking = res.data.viewerSubmission?.ranking
          if (Array.isArray(viewerRanking) && viewerRanking.length === ids.length) {
            setRankIds(viewerRanking.map((id) => String(id)))
            setHasSubmitted(true)
            setShowEditor(false)
            setEditorMode(null)
            setIntroEnabled(true)
          } else {
            setHasSubmitted(false)
            setShowEditor(true)
            setEditorMode('main')
            setRankIds(shuffleIds(ids))
            setIntroEnabled(true)
          }
        }
      })
      .catch((e) => err(e))
      .finally(() => setLoading(false))
  }, [pollId, userId, initialPoll])

  useEffect(() => {
    if (!tagOptions.length) {
      getPollTags()
        .then((res) => {
          if (res.success && res.data?.length) {
            setTagOptions(res.data)
          }
        })
        .catch(() => {
          // ignore
        })
    }

    if (!categoryOptions.length) {
      getPollCategories()
        .then((res) => {
          if (res.success && res.data?.length) {
            setCategoryOptions(res.data)
          }
        })
        .catch(() => {
          // ignore
        })
    }
  }, [tagOptions.length, categoryOptions.length])

  const scheduleMetaSave = (nextMeta: {
    category?: string
    tags?: string
    prompt?: string
    visibility?: string
    pollResultsVisibility?: 'hidden' | 'visible'
  }) => {
    setMetaDraft((prev) => ({
      category: nextMeta.category ?? prev.category,
      tags: nextMeta.tags ?? prev.tags,
      visibility: nextMeta.visibility ?? prev.visibility,
      pollResultsVisibility: nextMeta.pollResultsVisibility ?? prev.pollResultsVisibility,
    }))
  }

  useEffect(() => {
    if (!poll) return
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setMetaDraft({
      category: poll.category ?? '',
      tags: poll.tags ?? '',
      visibility: poll.visibility ?? 'public',
      pollResultsVisibility: poll.pollResultsVisibility || 'visible',
    })
  }, [poll?.category, poll?.tags, poll?.visibility, poll?.pollResultsVisibility])

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setItemDrafts((poll?.items || []).map((item) => ({ id: item.id, title: item.title })))
  }, [poll?.id, poll?.items])

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setCommunityResultsOpen(false)
  }, [poll?.id])

  useEffect(() => {
    if (!poll?.id) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setPollRated(undefined)
      return
    }
    const stored = localStorage.getItem(`poll_rating_${poll.id}`)
    if (stored === '0' || stored === '1') {
      setPollRated(Number(stored))
    } else {
      setPollRated(undefined)
    }
  }, [poll?.id])

  const isMetaDirty = useMemo(() => {
    if (!poll) return false
    return (
      (metaDraft.category ?? '') !== (poll.category ?? '') ||
      (metaDraft.tags ?? '') !== (poll.tags ?? '') ||
      (metaDraft.visibility ?? 'public') !== (poll.visibility ?? 'public') ||
      (metaDraft.pollResultsVisibility ?? 'visible') !== (poll.pollResultsVisibility ?? 'visible')
    )
  }, [metaDraft, poll])

  const handleUpdateMeta = () => {
    if (!poll?.id || !isOwner || !isMetaDirty) return
    setIsUpdatingMeta(true)
    updatePoll({
      pollId: poll.id!,
      category: metaDraft.category,
      tags: metaDraft.tags,
      visibility: metaDraft.visibility,
      pollResultsVisibility: metaDraft.pollResultsVisibility,
    })
      .then((res) => {
        if (res.success && res.data) {
          setPoll(res.data)
        }
      })
      .finally(() => {
        setIsUpdatingMeta(false)
      })
  }

  const handleDeletePoll = async () => {
    if (!poll?.id) return
    const res = await deletePoll(poll.id)
    if (res.success) {
      setDeleteOpen(false)
      onPollDeleted?.(poll.id)
      onBack?.()
    }
  }

  const handleUpdateItems = () => {
    if (!poll?.id || !isOwner) return
    const cleaned = itemDrafts
      .map((item) => ({ id: item.id, title: item.title.trim() }))
      .filter((item) => item.title.length > 0)
    if (cleaned.length < 10 || cleaned.length > 20) return
    setIsUpdatingItems(true)
    updatePollItems({
      pollId: poll.id,
      items: cleaned,
    })
      .then((res) => {
        if (res.success && res.data) {
          setPoll(res.data)
        }
      })
      .catch((e) => err(e))
      .finally(() => setIsUpdatingItems(false))
  }

  const handleRatePoll = (rating: 0 | 1) => {
    if (!poll?.id) return
    if (!userId) return
    setPollRated(rating)
    ratePoll({
      pollId: poll.id,
      rating,
      localKey: `poll_rating_${poll.id}`,
    })
      .then((res) => {
        if (res.success && res.data) {
          setPoll((prev) => (prev ? { ...prev, ...res.data } : prev))
        }
        localStorage.setItem(`poll_rating_${poll.id}`, String(rating))
      })
      .catch((e) => err(e))
  }

  const handleDragEnd = (event: {
    active: { id: string | number }
    over?: { id: string | number } | null
  }) => {
    if (isIntroAnimating) return
    if (!event.over || event.active.id === event.over.id) return
    const oldIndex = rankIds.indexOf(String(event.active.id))
    const newIndex = rankIds.indexOf(String(event.over.id))
    if (oldIndex === -1 || newIndex === -1) return
    setRankIds(arrayMove(rankIds, oldIndex, newIndex))
    sounds?.drag?.()
  }

  const lastDragOverId = useRef<string | null>(null)
  const lastDragOverAt = useRef<number>(0)

  const handleDragStart = () => {
    if (isIntroAnimating) return
    sounds?.drag?.()
  }

  const handleDragOver = (event: { over?: { id: string | number } | null }) => {
    if (isIntroAnimating) return
    if (!event.over) return
    const overId = String(event.over.id)
    if (overId === lastDragOverId.current) return
    const now = Date.now()
    if (now - lastDragOverAt.current < 80) return
    lastDragOverId.current = overId
    lastDragOverAt.current = now
    sounds?.drag?.()
  }

  const handleSwapClick = (id: string) => {
    if (isIntroAnimating) return
    if (!swapFromId) {
      setSwapFromId(id)
      return
    }
    if (swapFromId === id) {
      setSwapFromId(null)
      setSwapHoverId(null)
      return
    }
    const fromIndex = rankIds.indexOf(swapFromId)
    const toIndex = rankIds.indexOf(id)
    if (fromIndex === -1 || toIndex === -1) {
      setSwapFromId(null)
      setSwapHoverId(null)
      return
    }
    if (rankIds[toIndex] === undefined || rankIds[fromIndex] === undefined) return
    const direction =
      toIndex > fromIndex
        ? { [swapFromId]: 'down' as const, [id]: 'up' as const }
        : { [swapFromId]: 'up' as const, [id]: 'down' as const }

    setSwapAnimatingIds([swapFromId, id])
    setSwapAnimationDirectionById(direction)
    setSwapPhase('out')
    sounds?.drag?.()

    window.setTimeout(() => {
      const next = rankIds.slice()
      if (rankIds[toIndex] === undefined || rankIds[fromIndex] === undefined) return
      next[fromIndex] = rankIds[toIndex]
      next[toIndex] = rankIds[fromIndex]
      setRankIds(next)
      setSwapPhase('in')

      window.setTimeout(() => {
        setSwapAnimatingIds([])
        setSwapAnimationDirectionById({})
        setSwapPhase(null)
      }, 220)
    }, 160)

    setSwapFromId(null)
    setSwapHoverId(null)
  }

  const handleUpdateMain = () => {
    if (!poll?.items?.length) return
    const ids = poll.items.map((item) => String(item.id ?? item.index))
    const viewerRanking = poll.viewerSubmission?.ranking
    if (Array.isArray(viewerRanking) && viewerRanking.length === ids.length) {
      setRankIds(viewerRanking.map((id) => String(id)))
      setIntroEnabled(true)
    } else {
      setRankIds(shuffleIds(ids))
      setIntroEnabled(true)
    }
    setEditorMode('main')
    setShowEditor(true)
  }

  const handleRetractMain = () => {
    if (!poll?.id) return
    const ids = poll.items?.map((item) => String(item.id ?? item.index)) || []
    removePollSubmission({
      pollId: poll.id,
      localKey: userId ? undefined : getGuestKey(),
    })
      .then((res) => {
        if (res.success && res.data) {
          setPoll((prev) => {
            if (!res.data) return prev
            return {
              ...prev,
              ...res.data,
              items: prev?.items || res.data.items,
            }
          })
          setHasSubmitted(false)
          setEditorMode('main')
          setShowEditor(true)
          if (ids.length) {
            setRankIds(shuffleIds(ids))
            setIntroEnabled(true)
          }
        }
      })
      .catch((e) => err(e))
  }

  const handleNewExtra = () => {
    if (!poll?.items?.length) return
    const ids = poll.items.map((item) => String(item.id ?? item.index))
    setEditingExtraIndex(null)
    setEditorMode('extra')
    setShowEditor(true)
    setRankIds(shuffleIds(ids))
    setIntroEnabled(true)
  }

  const handleEditExtra = (index?: number) => {
    if (!index) return
    const entry = ownerExtras.find((extra) => extra.ownerExtraIndex === index)
    if (entry && Array.isArray(entry.ranking)) {
      setRankIds(entry.ranking.map((id) => String(id)))
      setIntroEnabled(true)
    }
    setEditingExtraIndex(index)
    setEditorMode('extra')
    setShowEditor(true)
  }

  const handleRetractExtra = (index?: number) => {
    if (!poll?.id) return
    removePollSubmission({
      pollId: poll.id,
      ownerExtraIndex: index,
    })
      .then((res) => {
        if (res.success && res.data) {
          setPoll((prev) => {
            if (!res.data) return prev
            return {
              ...prev,
              ...res.data,
              items: prev?.items || res.data.items,
            }
          })
          if (index) {
            setOwnerExtras((prev) => prev.filter((extra) => extra.ownerExtraIndex !== index))
          }
        }
      })
      .catch((e) => err(e))
  }

  const refreshPoll = () => {
    const localKey = getGuestKey()
    return getPoll(pollId, localKey ? { localKey } : undefined)
      .then((res) => {
        if (res.success && res.data) {
          setPoll(res.data)
          setOwnerExtras(res.data.ownerExtras || [])
          setHasSubmitted(!!res.data.viewerSubmission)
        }
      })
      .catch((e) => err(e))
  }

  const handleSubmit = () => {
    if (!poll?.id || !editorMode) return

    if (editorMode === 'extra') {
      if (!isOwner) return
      submitPoll({
        pollId: poll.id,
        ranking: rankIds,
        ownerSubmission: true,
        ownerExtraIndex: editingExtraIndex || undefined,
      })
        .then((res) => {
          if (res.success && res.data) {
            setEditingExtraIndex(null)
            setEditorMode(null)
            setShowEditor(false)
            refreshPoll()
          }
        })
        .catch((e) => err(e))
      return
    }

    submitPoll({
      pollId: poll.id,
      ranking: rankIds,
      localKey: userId ? undefined : getGuestKey(),
      ownerSubmission: false,
    })
      .then((res) => {
        if (res.success && res.data) {
          setHasSubmitted(true)
          setShowEditor(false)
          setEditorMode(null)
          refreshPoll()
        }
      })
      .catch((e) => err(e))
  }

  const isLoading = loading || externalLoading

  const handleTopBack = () => {
    if (showEditor && hasSubmitted && onBackToDetail) {
      setShowEditor(false)
      setEditorMode(null)
      setEditingExtraIndex(null)
      onBackToDetail()
      return
    }
    ;(onPollBack || onBack)?.()
  }
  const resolvedBackLabel = showEditor && hasSubmitted && onBackToDetail ? 'Poll' : pollBackLabel

  if (isLoading) {
    return (
      <PollLoadingView
        onBack={handleTopBack}
        backLabel={resolvedBackLabel}
        onNewPoll={onNewPoll}
        statusLabel={topRightLabel}
      />
    )
  }

  if (!poll) {
    return (
      <Box py={8} display="flex" justifyContent="center">
        <Typography>No poll found.</Typography>
      </Box>
    )
  }

  // const stateLabel = poll.state === 'collecting' ? 'Collecting Ranks' : 'Results Open'
  const swapActive = swapFromId !== null
  const selectedIndex = swapFromId ? rankIds.indexOf(swapFromId) : -1
  const hoveredIndex = swapHoverId ? rankIds.indexOf(swapHoverId) : -1
  const hasHoverTarget = Boolean(swapHoverId && hoveredIndex !== -1 && selectedIndex !== -1)
  const selectedDirection = hasHoverTarget && hoveredIndex > selectedIndex ? 'down' : 'up'
  const hoveredDirection = hasHoverTarget && hoveredIndex > selectedIndex ? 'up' : 'down'

  return (
    <PollDesign
      sx={{
        '& .rt-poll-section': {
          p: { xs: 2, sm: 3 },
          background: '#fff',
          border: '1px solid #efddc9',
          borderRadius: '24px',
          textAlign: 'left',
          '& .MuiTypography-root': { textAlign: 'left' },
          boxShadow: '0 3px 0 #efddc9',
        },
        '& .rt-poll-section h2, & .editor-owner-settings h2': {
          color: '#693413',
          fontFamily: '"Fredoka", sans-serif',
          fontWeight: 600,
          fontSize: { xs: 23, sm: 26 },
        },
        '& .editor-owner-settings .editor-meta': {
          background: '#ffefdd',
          borderColor: '#efddc9',
          boxShadow: '0 3px 0 #e8bb99',
        },
        '& .editor-owner-settings .editor-main > *': {
          background: '#fff',
          border: '1px solid #efddc9',
          boxShadow: '0 3px 0 #efddc9',
        },
        '& .editor-owner-settings .MuiTextField-root .MuiOutlinedInput-root': {
          background: '#fffaf5',
          color: '#693413',
          '& .MuiOutlinedInput-notchedOutline': { borderColor: '#e8cdb2' },
          '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#d4a27a' },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#e98549' },
          '&.Mui-disabled': { background: '#fff4e8' },
        },
        '& .editor-owner-settings .MuiInputLabel-root, & .editor-owner-settings .MuiFormHelperText-root':
          { color: '#855c3d' },
        '& .editor-owner-settings .MuiSelect-icon, & .editor-owner-settings .MuiAutocomplete-popupIndicator':
          { color: '#944217' },
        '& .editor-owner-settings .MuiTypography-body2, & .editor-owner-settings .MuiTypography-caption':
          { color: '#855c3d' },
      }}
    >
      <Stack pb={2} spacing={2} sx={{ '& > .rt-view-navigation + *': { mt: '10px' } }}>
        <ViewNavigation mb={0}>
          <GlassOutlineButton
            variant="outlined"
            onClick={handleTopBack}
            sx={{
              pl: 2,
              pr: 3,
              py: 0.75,
              color: '#60768b',
              fontWeight: 600,
              boxShadow: shadow1,
            }}
            startIcon={<ArrowBackRoundedIcon fontSize="inherit" sx={{ color: '#7c91a3' }} />}
          >
            {resolvedBackLabel}
          </GlassOutlineButton>
          {topRightLabel ? (
            <GlassOutlineButton
              variant="outlined"
              disabled
              sx={{
                pl: 2,
                pr: 2.5,
                py: 0.75,
                fontWeight: 600,
                boxShadow: shadow1,
                minWidth: '140px',
                whiteSpace: 'nowrap',
                color: helperTextColor,
                '&.Mui-disabled': {
                  color: '#60768b',
                  opacity: 0.9,
                },
              }}
            >
              {topRightLabel}
            </GlassOutlineButton>
          ) : onNewPoll ? (
            <PollNavButton
              onClick={(event) => {
                ;(event.currentTarget as HTMLElement).blur()
                onNewPoll?.()
              }}
              onTouchEnd={(event) => {
                ;(event.currentTarget as HTMLElement).blur()
              }}
              disabled={isLoading}
              startIcon={<AutorenewRoundedIcon fontSize="inherit" />}
            >
              New Poll
            </PollNavButton>
          ) : isOwner ? (
            <GlassOutlineButton
              variant="outlined"
              onClick={onOpenHowToPlay}
              sx={{
                pl: 2,
                pr: 2.5,
                py: 0.75,
                fontWeight: 600,
                boxShadow: shadow1,
                minWidth: '140px',
                whiteSpace: 'nowrap',
                color: helperTextColor,
              }}
              startIcon={<InfoOutlinedIcon fontSize="inherit" sx={{ color: '#7c91a3' }} />}
            >
              How to Play
            </GlassOutlineButton>
          ) : (
            <Box />
          )}
        </ViewNavigation>
        <Box ref={promptRef}>
          <PollDetailsHero
            poll={poll}
            voting={showEditor}
            isOwner={isOwner}
            canViewResults={canViewCommunityResults}
            onPlay={onPlayPoll}
          />
        </Box>

        {hasSubmitted && !showEditor ? (
          <GlassCard className="rt-poll-section">
            <Stack spacing={2}>
              <Box display="flex" gap={1.5} alignItems="center">
                <Box
                  sx={{
                    width: 44,
                    height: 44,
                    flexShrink: 0,
                    display: 'grid',
                    placeItems: 'center',
                    borderRadius: '14px',
                    background: '#ffefdd',
                    color: '#944217',
                    boxShadow: '0 3px 0 #e8bb99',
                  }}
                >
                  <HowToVoteRoundedIcon />
                </Box>
                <Box>
                  <Typography variant="h2">Your Ranking</Typography>
                  <Typography variant="body2" sx={{ color: '#855c3d', mt: 0.5 }}>
                    Your vote is in. Here’s how you ranked it.
                  </Typography>
                </Box>
              </Box>
              {viewerRankingTitles.length ? (
                <Stack component="ol" spacing={0.75} sx={{ listStyle: 'none', p: 0, m: 0 }}>
                  {viewerRankingTitles.map((title, idx) => (
                    <Box
                      component="li"
                      key={idx}
                      sx={{
                        display: 'flex',
                        gap: 1.5,
                        alignItems: 'center',
                        p: 1,
                        background: '#fffaf5',
                        borderRadius: '12px',
                      }}
                    >
                      <Box
                        sx={{
                          width: 28,
                          height: 28,
                          flexShrink: 0,
                          display: 'grid',
                          placeItems: 'center',
                          background: '#ffefdd',
                          color: '#944217',
                          borderRadius: '9px',
                          fontWeight: 700,
                          fontSize: 13,
                        }}
                      >
                        {idx + 1}
                      </Box>
                      <Typography
                        variant="body2"
                        sx={{ color: '#693413', overflowWrap: 'anywhere' }}
                      >
                        {title}
                      </Typography>
                    </Box>
                  ))}
                </Stack>
              ) : null}
              <Box
                display="flex"
                gap={1}
                flexWrap="wrap"
                alignItems="center"
                justifyContent="space-between"
              >
                <Button
                  color="error"
                  startIcon={<ReplyRoundedIcon />}
                  onClick={() => {
                    sounds?.click?.()
                    handleRetractMain()
                  }}
                >
                  Retract Vote
                </Button>
                <Button
                  variant="outlined"
                  color="secondary"
                  endIcon={<HowToVoteRoundedIcon />}
                  onClick={() => {
                    sounds?.click?.()
                    handleUpdateMain()
                  }}
                  sx={{ color: '#944217', borderColor: '#e8cdb2', fontWeight: 700 }}
                >
                  Update Vote
                </Button>
              </Box>
            </Stack>
          </GlassCard>
        ) : null}

        {!showEditor ? (
          <GlassCard className="rt-poll-section">
            <Stack spacing={2}>
              <Box display="flex" alignItems="center" gap={1.5}>
                <Box
                  sx={{
                    width: 44,
                    height: 44,
                    flexShrink: 0,
                    display: 'grid',
                    placeItems: 'center',
                    borderRadius: '14px',
                    background: '#ffefdd',
                    color: '#944217',
                    boxShadow: '0 3px 0 #e8bb99',
                  }}
                >
                  <GroupsRoundedIcon />
                </Box>
                <Box>
                  <Typography variant="h2">Community Results</Typography>
                  <Typography variant="body2" sx={{ color: '#855c3d', mt: 0.5 }}>
                    Average from {poll.submissions || 0} votes.
                  </Typography>
                </Box>
              </Box>
              {!canViewCommunityResults ? (
                <Box sx={{ p: 2, borderRadius: '16px', background: '#fff1ee', color: '#9d4043' }}>
                  <Box display="flex" gap={1} alignItems="center" mb={0.75}>
                    <VisibilityOffRoundedIcon fontSize="small" />
                    <Typography fontWeight={700}>Results Hidden</Typography>
                  </Box>
                  <Typography variant="body2">
                    {poll.dailyPollType === 'today'
                      ? 'Check back tomorrow to see what everyone picked.'
                      : 'The poll owner hasn’t opened results yet.'}
                  </Typography>
                </Box>
              ) : (
                <>
                  <Box
                    sx={{
                      display: 'flex',
                      flexWrap: 'wrap',
                      gap: 1.5,
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      p: 2,
                      borderRadius: '16px',
                      background: '#ffefdd',
                    }}
                  >
                    <Typography variant="body2" sx={{ color: '#855c3d', flex: '1 1 160px' }}>
                      {communityResultsOpen
                        ? 'Here’s how the community voted.'
                        : 'Play first or peek at the picks. No accidental spoilers.'}
                    </Typography>
                    <Button
                      variant="outlined"
                      color="secondary"
                      startIcon={
                        communityResultsOpen ? (
                          <VisibilityOffRoundedIcon />
                        ) : (
                          <VisibilityRoundedIcon />
                        )
                      }
                      onClick={() => {
                        sounds?.click?.()
                        setCommunityResultsOpen((prev) => !prev)
                      }}
                      sx={{
                        color: '#944217',
                        borderColor: '#e8bb99',
                        background: '#fffaf5',
                        fontWeight: 700,
                      }}
                    >
                      {communityResultsOpen ? 'Hide Results' : 'View Results'}
                    </Button>
                  </Box>
                  <Collapse in={communityResultsOpen} timeout={250} unmountOnExit>
                    {communityResultsRows.length ? (
                      <Stack component="ol" spacing={1} sx={{ listStyle: 'none', p: 0, m: 0 }}>
                        {communityResultsRows.map((row) => (
                          <Box
                            component="li"
                            key={row.id}
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 1.25,
                              p: 1.25,
                              borderRadius: '14px',
                              background: '#fffaf5',
                              border: '1px solid #efddc9',
                            }}
                          >
                            <Box
                              sx={{
                                width: 30,
                                height: 30,
                                flexShrink: 0,
                                display: 'grid',
                                placeItems: 'center',
                                borderRadius: '10px',
                                background: row.rank <= 3 ? '#ffac78' : '#ffefdd',
                                color: '#693413',
                                fontWeight: 700,
                              }}
                            >
                              {row.rank}
                            </Box>
                            <Box flex={1} minWidth={0}>
                              <Typography
                                variant="body2"
                                sx={{ color: '#693413', fontWeight: 600, overflowWrap: 'anywhere' }}
                              >
                                {row.title}
                              </Typography>
                              <Box display="flex" gap={0.5} flexWrap="wrap">
                                {mostControversialId === row.id ? (
                                  <Tooltip title="Most varied across votes.">
                                    <Chip
                                      size="small"
                                      label="Controversial"
                                      sx={{
                                        mt: 0.5,
                                        background: '#ffe4e1',
                                        color: '#9d4043',
                                        fontSize: 10,
                                      }}
                                    />
                                  </Tooltip>
                                ) : null}
                                {maxTop3Count > 0 && crowdFavoriteId === row.id ? (
                                  <Tooltip title="Most frequently ranked in the Top 3 by voters.">
                                    <Chip
                                      size="small"
                                      label="Crowd Favorite"
                                      sx={{
                                        mt: 0.5,
                                        background: '#ffefdd',
                                        color: '#944217',
                                        fontSize: 10,
                                      }}
                                    />
                                  </Tooltip>
                                ) : null}
                              </Box>
                            </Box>
                            <Box textAlign="right" flexShrink={0}>
                              <Typography fontWeight={700} sx={{ color: '#693413', fontSize: 16 }}>
                                {row.average ? row.average.toFixed(2) : '—'}
                              </Typography>
                              <Typography variant="caption" sx={{ color: '#855c3d', fontSize: 10 }}>
                                avg. rank
                              </Typography>
                            </Box>
                          </Box>
                        ))}
                      </Stack>
                    ) : (
                      <Typography variant="body2" sx={{ color: '#855c3d' }}>
                        Community results are not available yet.
                      </Typography>
                    )}
                  </Collapse>
                </>
              )}
            </Stack>
          </GlassCard>
        ) : null}

        {!showEditor ? (
          <GlassCard className="rt-poll-section">
            <Box
              display="flex"
              gap={2}
              flexWrap="wrap"
              alignItems="center"
              justifyContent="space-between"
            >
              <Box>
                <Typography variant="h2">Was this fun?</Typography>
                <Typography variant="body2" sx={{ color: '#855c3d', mt: 0.5 }}>
                  {userId ? 'Help the good polls rise to the top.' : 'Log in to rate'}
                </Typography>
              </Box>
              <Box display="flex" gap={1} flexWrap="wrap">
                <Button
                  disabled={!userId}
                  aria-pressed={pollRated === 1}
                  variant="outlined"
                  color="secondary"
                  startIcon={<ThumbUpRoundedIcon />}
                  onClick={() => {
                    sounds?.click?.()
                    handleRatePoll(1)
                  }}
                  sx={{
                    color: '#944217',
                    borderColor: '#e8cdb2',
                    background: pollRated === 1 ? '#ffefdd' : 'transparent',
                  }}
                >
                  Fun
                </Button>
                <Button
                  disabled={!userId}
                  aria-pressed={pollRated === 0}
                  variant="outlined"
                  color="secondary"
                  startIcon={<ThumbDownRoundedIcon />}
                  onClick={() => {
                    sounds?.click?.()
                    handleRatePoll(0)
                  }}
                  sx={{
                    color: '#944217',
                    borderColor: '#e8cdb2',
                    background: pollRated === 0 ? '#ffefdd' : 'transparent',
                  }}
                >
                  Not Fun
                </Button>
              </Box>
            </Box>
          </GlassCard>
        ) : null}

        {isOwner && !showEditor ? (
          <EditorGrid className="editor-poll editor-owner-settings">
            <Box className="editor-meta" sx={{ minWidth: 0 }}>
              <GlassPanel
                sx={{
                  p: { xs: 1, sm: 2 },
                }}
              >
                <Stack spacing={1}>
                  <EditorSectionHeading
                    title="Discoverability"
                    description="Manage who can find your poll."
                    poll
                  />
                  <Box display="flex" gap={1} flexWrap="wrap" width="100%" alignItems="center">
                    <Autocomplete
                      fullWidth
                      size="small"
                      onChange={(_e, v) => {
                        const nextCategories = normalizeMetaList(v).join(',')
                        scheduleMetaSave({
                          category: nextCategories,
                          tags: metaDraft.tags,
                          visibility: metaDraft.visibility,
                          pollResultsVisibility: metaDraft.pollResultsVisibility,
                        })
                      }}
                      value={selectedCategories}
                      freeSolo={true}
                      multiple={true}
                      options={mergedCategoryOptions}
                      sx={{ minWidth: { xs: '100%', sm: 260 } }}
                      renderInput={({ InputProps, ...params }) => (
                        <TextField
                          InputProps={{
                            ...InputProps,
                            ...{
                              sx: {
                                background: '#fffaf5',
                                borderRadius: '14px',
                              },
                            },
                          }}
                          {...params}
                          label="Categories"
                          placeholder="Categories"
                        />
                      )}
                      renderOption={(props, option) => (
                        <li {...props} key={option}>
                          {option}
                        </li>
                      )}
                      renderTags={(value: readonly string[], getTagProps) =>
                        value.map((option: string, index: number) => {
                          const { key, ...tagProps } = getTagProps({ index })
                          return (
                            <Chip
                              size="small"
                              variant="filled"
                              label={normalizeMetaValue(option)}
                              key={key}
                              deleteIcon={
                                <ClearRoundedIcon
                                  fontSize="inherit"
                                  sx={{ fontSize: '13px !important' }}
                                />
                              }
                              sx={{
                                background: mainColor,
                                color: mainColorMediumText,
                                fontWeight: 600,
                                boxShadow: shadow2,
                              }}
                              {...tagProps}
                            />
                          )
                        })
                      }
                    />
                    <Autocomplete
                      fullWidth
                      size="small"
                      onChange={(_e, v) => {
                        const nextTags = normalizeMetaList(v).join(',')
                        scheduleMetaSave({
                          category: metaDraft.category,
                          tags: nextTags,
                          visibility: metaDraft.visibility,
                          pollResultsVisibility: metaDraft.pollResultsVisibility,
                        })
                      }}
                      value={selectedTags}
                      freeSolo
                      multiple={true}
                      options={mergedTagOptions}
                      sx={{ minWidth: { xs: '100%', sm: 260 } }}
                      renderInput={({ InputProps, ...params }) => (
                        <TextField
                          InputProps={{
                            ...InputProps,
                            ...{
                              sx: {
                                background: '#fffaf5',
                                borderRadius: '12px',
                              },
                            },
                          }}
                          {...params}
                          label="Tags"
                          placeholder="Tags"
                        />
                      )}
                      renderOption={(props, option) => (
                        <li {...props} key={option}>
                          {option}
                        </li>
                      )}
                      renderTags={(value: readonly string[], getTagProps) =>
                        value.map((option: string, index: number) => {
                          const { key, ...tagProps } = getTagProps({ index })
                          return (
                            <Chip
                              size="small"
                              variant="filled"
                              label={normalizeMetaValue(option)}
                              key={key}
                              deleteIcon={
                                <ClearRoundedIcon
                                  fontSize="inherit"
                                  sx={{ fontSize: '13px !important' }}
                                />
                              }
                              sx={{
                                background: altColor,
                                color: altColorDarkText,
                                fontWeight: 600,
                                boxShadow: shadow2,
                              }}
                              {...tagProps}
                            />
                          )
                        })
                      }
                    />

                    <Stack width="100%">
                      <TextField
                        select
                        fullWidth
                        size="small"
                        label="Visibility"
                        value={metaDraft.visibility || 'public'}
                        onChange={(e) => {
                          const nextVisibility = e.target.value
                          scheduleMetaSave({
                            category: metaDraft.category,
                            tags: metaDraft.tags,
                            visibility: nextVisibility,
                            pollResultsVisibility:
                              nextVisibility === 'public'
                                ? 'visible'
                                : metaDraft.pollResultsVisibility,
                          })
                        }}
                        InputProps={{
                          sx: {
                            background: '#fffaf5',
                            borderRadius: '12px',
                          },
                        }}
                        sx={{ minWidth: { xs: '100%', sm: 200 } }}
                      >
                        <MenuItem value="public">Public</MenuItem>
                        <MenuItem value="private">Private</MenuItem>
                      </TextField>
                      <Typography variant="caption" color={helperTextColor} textAlign="left" pl={1}>
                        {metaDraft.visibility === 'public'
                          ? 'Visible and playable by the community.'
                          : 'Private and only shareable by link.'}
                      </Typography>
                    </Stack>
                    <Stack width="100%">
                      <TextField
                        select
                        fullWidth
                        size="small"
                        label="Poll Results"
                        value={metaDraft.pollResultsVisibility}
                        onChange={(e) => {
                          const nextResultsVisibility = e.target.value as 'hidden' | 'visible'
                          scheduleMetaSave({
                            category: metaDraft.category,
                            tags: metaDraft.tags,
                            visibility: metaDraft.visibility,
                            pollResultsVisibility: nextResultsVisibility,
                          })
                        }}
                        disabled={metaDraft.visibility === 'public'}
                        InputProps={{
                          sx: {
                            background: '#fffaf5',
                            borderRadius: '12px',
                          },
                        }}
                        sx={{ minWidth: { xs: '100%', sm: 200 } }}
                      >
                        <MenuItem value="hidden">Hidden</MenuItem>
                        <MenuItem value="visible">Visible</MenuItem>
                      </TextField>
                      <Typography variant="caption" color={helperTextColor} textAlign="left" pl={1}>
                        {metaDraft.visibility === 'public'
                          ? 'Public polls lock results mode. Switch to Private to change this.'
                          : metaDraft.pollResultsVisibility === 'hidden'
                            ? 'Users can vote/play, but cannot view community results yet.'
                            : 'Community results are visible on this poll.'}
                      </Typography>
                    </Stack>

                    <Box display="flex" justifyContent="flex-end" alignItems="center" width="100%">
                      {isUpdatingMeta ? (
                        <Typography
                          variant="body2"
                          sx={{
                            color: '#944217',
                            borderColor: '#e8cdb2',
                            boxShadow: '0 2px 0 #efddc9',
                          }}
                        >
                          Saving…
                        </Typography>
                      ) : null}
                      {!isUpdatingMeta ? (
                        <GlassButton
                          variant="contained"
                          color="secondary"
                          onClick={() => {
                            sounds?.click?.()
                            handleUpdateMeta()
                          }}
                          disabled={!isMetaDirty}
                          sx={{ minWidth: 120 }}
                        >
                          <Box display="flex" alignItems="center" gap={1}>
                            Update
                            <SaveRoundedIcon fontSize="small" color="inherit" />
                          </Box>
                        </GlassButton>
                      ) : null}
                    </Box>
                  </Box>
                </Stack>
              </GlassPanel>
            </Box>
            <Box className="editor-main">
              <GlassPanel sx={{ p: { xs: 1.5, sm: 2 } }}>
                <Stack spacing={1.5}>
                  <Box display="flex" justifyContent="space-between" alignItems="center">
                    <EditorSectionHeading
                      title="Poll options"
                      description="Refine your community ranking."
                      poll
                    />
                    <Typography variant="caption" color={helperTextColor} pr={1}>
                      {itemDrafts.length}/20
                    </Typography>
                  </Box>
                  <Stack spacing={1}>
                    {itemDrafts.map((item, idx) => (
                      <TextField
                        key={`poll-item-${item.id || idx}`}
                        size="small"
                        fullWidth
                        value={item.title}
                        onChange={(e) => {
                          const nextTitle = e.target.value
                          setItemDrafts((prev) =>
                            prev.map((entry, entryIdx) =>
                              entryIdx === idx ? { ...entry, title: nextTitle } : entry
                            )
                          )
                        }}
                        placeholder={`Option ${idx + 1}`}
                        inputProps={{ maxLength: 255 }}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <Typography
                                variant="caption"
                                sx={{ color: '#944217', fontWeight: 700, width: 18 }}
                              >
                                {idx + 1}
                              </Typography>
                            </InputAdornment>
                          ),
                          sx: {
                            background: '#fffaf5',
                            borderRadius: '12px',
                          },
                        }}
                      />
                    ))}
                  </Stack>
                  <Box display="flex" flexWrap="wrap" gap={1.5} justifyContent="space-between" alignItems="center">
                    <GlassOutlineButton
                      variant="outlined"
                      color="secondary"
                      onClick={() => {
                        if (itemDrafts.length >= 20) return
                        sounds?.click?.()
                        setItemDrafts((prev) => [...prev, { title: '' }])
                      }}
                      disabled={itemDrafts.length >= 20}
                      startIcon={<AddCircleOutlineRoundedIcon fontSize="small" />}
                    >
                      Add Option
                    </GlassOutlineButton>
                    <GlassButton
                      variant="contained"
                      color="secondary"
                      onClick={() => {
                        sounds?.click?.()
                        handleUpdateItems()
                      }}
                      disabled={
                        isUpdatingItems ||
                        itemDrafts.filter((item) => item.title.trim().length > 0).length < 10 ||
                        itemDrafts.filter((item) => item.title.trim().length > 0).length > 20
                      }
                      sx={{ minWidth: 150 }}
                    >
                      <Box display="flex" alignItems="center" gap={1}>
                        {isUpdatingItems ? 'Saving…' : 'Update Options'}
                        <SaveRoundedIcon fontSize="small" color="inherit" />
                      </Box>
                    </GlassButton>
                  </Box>
                  <Typography variant="caption" color={helperTextColor} textAlign="left" px={1}>
                    Added options cannot be deleted. Gameplay uses the top 10 voted options.
                  </Typography>
                </Stack>
              </GlassPanel>
            </Box>
          </EditorGrid>
        ) : null}

        {isOwner && hasSubmitted && !showEditor ? (
          <Stack>
            <GlassCard
              className="rt-poll-section"
              sx={{
                p: { xs: 2, sm: 3 },
                backgroundColor: '#ffefdd',
                border: '1px solid #efddc9',
                boxShadow: '0 3px 0 #e8bb99',
              }}
            >
              <Stack spacing={2}>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                  <Box display="flex" alignItems="center" gap={1.5}>
                    <Avatar
                      sx={{
                        backgroundColor: '#fffaf5',
                        color: '#944217',
                        p: 0.33,
                      }}
                    >
                      <GroupsRoundedIcon
                        sx={{
                          fontSize: '30px',
                        }}
                      />
                    </Avatar>
                    <Stack>
                      <Typography variant="h2" sx={{ fontSize: 24, color: '#693413' }}>
                        Extra Submissions
                      </Typography>
                      <Typography variant="body2" color="#855c3d" textAlign="left">
                        Submit extra votes from friends!
                      </Typography>
                    </Stack>
                  </Box>
                  <GlassOutlineButton
                    variant="outlined"
                    onClick={() => {
                      sounds?.click?.()
                      handleNewExtra()
                    }}
                    sx={{
                      color: '#944217',
                      display: {
                        xs: 'none',
                        sm: 'flex',
                      },
                    }}
                    endIcon={<HowToVoteRoundedIcon />}
                  >
                    New Submission
                  </GlassOutlineButton>
                  <IconButton
                    onClick={() => {
                      sounds?.click?.()
                      handleNewExtra()
                    }}
                    sx={{
                      backgroundColor: '#FFF',
                      border: '1px solid #efddc9',
                      boxShadow: '0 3px 0 #e8bb99',
                      color: '#944217',
                      '&:hover': { backgroundColor: '#fff6ed' },
                      display: {
                        xs: 'flex',
                        sm: 'none',
                      },
                    }}
                  >
                    <AddIcon />
                  </IconButton>
                </Box>
                {ownerExtras.length === 0 ? (
                  <Chip
                    sx={{ backgroundColor: '#fff3e6', color: '#855c3d' }}
                    label={
                      <Typography variant="body2" color="#855c3d" fontWeight={500}>
                        No extra submissions yet.
                      </Typography>
                    }
                  ></Chip>
                ) : (
                  <Stack spacing={1}>
                    {ownerExtras.map((extra, idx) => (
                      <GlassPanel
                        key={extra.id || extra.ownerExtraIndex || idx}
                        sx={{
                          background: '#fffaf5',
                          borderColor: '#efddc9',
                          boxShadow: '0 2px 0 #e8bb99',
                          px: 1.5,
                          py: 1,
                          borderRadius: '16px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          gap: 1,
                        }}
                      >
                        <Typography variant="body2" fontWeight={600} color="#693413">
                          Extra Submission {extra.ownerExtraIndex || idx + 1}
                        </Typography>
                        <Box display="flex" gap={1}>
                          <Button
                            variant="text"
                            onClick={() => {
                              sounds?.click?.()
                              handleRetractExtra(extra.ownerExtraIndex)
                            }}
                            color="error"
                            endIcon={<ReplyRoundedIcon />}
                          >
                            Retract Vote
                          </Button>
                          <GlassOutlineButton
                            variant="text"
                            onClick={() => {
                              sounds?.click?.()
                              handleEditExtra(extra.ownerExtraIndex)
                            }}
                          >
                            Update
                          </GlassOutlineButton>
                        </Box>
                      </GlassPanel>
                    ))}
                  </Stack>
                )}
              </Stack>
            </GlassCard>
          </Stack>
        ) : null}

        {showEditor ? (
          <Box pb={0}>
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragStart={handleDragStart}
              onDragOver={handleDragOver}
              onDragEnd={handleDragEnd}
            >
              <SortableContext items={rankIds} strategy={verticalListSortingStrategy}>
                <Stack spacing={1} sx={{ pointerEvents: isIntroAnimating ? 'none' : 'auto' }}>
                  {rankIds.map((id, idx) => {
                    const isSelected = swapFromId === id
                    const isHovered = swapHoverId === id
                    const swapIsCancel = isSelected && isHovered
                    const showSwapIcon =
                      swapActive && (isSelected || (isHovered && swapFromId !== id))
                    const swapIcon = showSwapIcon ? (
                      <Box
                        sx={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          width: 28,
                          height: 28,
                          borderRadius: '8px',
                          color: '#53d9f5',
                          background: isSelected
                            ? 'rgba(0, 179, 255,0.1)'
                            : 'rgba(148,163,184,0.12)',
                        }}
                      >
                        {swapIsCancel ? (
                          <CloseRoundedIcon fontSize="small" />
                        ) : isSelected ? (
                          hasHoverTarget ? (
                            <ReplyRoundedIcon
                              fontSize="small"
                              sx={{
                                transform:
                                  selectedDirection === 'down'
                                    ? 'scaleX(-1) rotate(-90deg)'
                                    : 'rotate(90deg)',
                              }}
                            />
                          ) : (
                            <SwapVertRoundedIcon fontSize="small" />
                          )
                        ) : hasHoverTarget ? (
                          <ReplyRoundedIcon
                            fontSize="small"
                            sx={{
                              transform:
                                hoveredDirection === 'down'
                                  ? 'scaleX(-1) rotate(-90deg)'
                                  : 'rotate(90deg)',
                            }}
                          />
                        ) : (
                          <SwapVertRoundedIcon fontSize="small" />
                        )}
                      </Box>
                    ) : null
                    const isVisible = idx < visibleCount
                    const introSx = isIntroAnimating
                      ? {
                          opacity: isVisible ? 1 : 0,
                          transform: isVisible ? 'translateY(0px)' : 'translateY(18px)',
                        }
                      : { opacity: 1, transform: 'translateY(0px)' }

                    return (
                      <Box
                        key={id}
                        sx={{
                          transition: 'opacity 220ms ease, transform 220ms ease',
                          ...introSx,
                        }}
                      >
                        <PollSortableItem
                          id={id}
                          index={idx}
                          title={itemsById[id] || ''}
                          onSwapClick={handleSwapClick}
                          onSwapHover={(hoverId) => {
                            if (!swapFromId) {
                              setSwapHoverId(null)
                              return
                            }
                            setSwapHoverId(hoverId)
                          }}
                          isSwapMode={swapActive}
                          isSwapSelected={isSelected}
                          isSwapHovered={swapActive && isHovered}
                          isDisabled={isIntroAnimating}
                          swapAnimationDirection={swapAnimationDirectionById[id]}
                          swapPhase={swapPhase}
                          swapControl={
                            swapIcon || (swapActive ? <Box sx={{ width: 20 }} /> : undefined)
                          }
                        />
                      </Box>
                    )
                  })}
                </Stack>
              </SortableContext>
            </DndContext>

            <Stack
              direction={{ xs: 'column-reverse', sm: 'row' }}
              spacing={1.5}
              mt={2}
              justifyContent="space-between"
            >
              <Box
                display="flex"
                justifyContent="space-between"
                width="100%"
                flexDirection={{
                  xs: 'column-reverse',
                  sm: 'column-reverse',
                }}
                gap={1}
              >
                {hasSubmitted || editorMode === 'extra' ? (
                  <Button
                    variant="text"
                    onClick={() => {
                      sounds?.click?.()
                      setShowEditor(false)
                      setEditorMode(null)
                      setEditingExtraIndex(null)
                    }}
                    color="error"
                    sx={{
                      borderRadius: '32px',
                      px: 2,
                      py: 1.5,
                      fontSize: 17,
                    }}
                  >
                    Cancel
                  </Button>
                ) : (
                  <Box></Box>
                )}
                <GlassButton
                  variant="contained"
                  onClick={() => {
                    sounds?.submit?.()
                    sounds?.celebrate?.()
                    handleSubmit()
                  }}
                  fullWidth
                  sx={{ fontWeight: 500, fontSize: 20 }}
                  endIcon={<HowToVoteRoundedIcon fontSize="medium" />}
                >
                  {editorMode === 'extra' ? 'Submit Vote' : 'Submit Vote'}
                </GlassButton>
              </Box>
            </Stack>
          </Box>
        ) : null}

        {isOwner && !showEditor ? (
          <Box display="flex" justifyContent="flex-start">
            <Button
              variant="text"
              color="error"
              sx={{
                border: 'none',
                borderRadius: '22px',
                px: 2,
                textTransform: 'none',
                fontSize: 17,
                py: 1.5,
              }}
              onClick={() => {
                sounds?.click?.()
                setDeleteOpen(true)
              }}
              endIcon={<DeleteForeverRoundedIcon fontSize="small" />}
            >
              Delete Poll
            </Button>
          </Box>
        ) : null}

        <Dialog open={deleteOpen} onClose={() => setDeleteOpen(false)}>
          <DialogTitle>Delete Poll?</DialogTitle>
          <DialogContent>
            <Typography variant="body2" color="#64748b">
              This will permanently delete this poll and all submissions. This cannot be undone.
            </Typography>
          </DialogContent>
          <DialogActions>
            <GlassOutlineButton
              variant="text"
              onClick={() => setDeleteOpen(false)}
              sx={{ textTransform: 'none' }}
            >
              Cancel
            </GlassOutlineButton>
            <GlassButton
              variant="contained"
              color="error"
              onClick={handleDeletePoll}
              sx={{ textTransform: 'none' }}
            >
              Delete
            </GlassButton>
          </DialogActions>
        </Dialog>
      </Stack>
    </PollDesign>
  )
}
